import { useMemo } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';

import {
    useAuthenticatedAxios,
    createAuthenticatedAxios,
} from './axiosInstance';

// hook for API operations
export const useUserApi = () => {
    const api = useAuthenticatedAxios();
    const { user } = useAuth();

    return useMemo(
        () => ({
            createProfile: async (profileData) => {
                const signupRefreshApi = createAuthenticatedAxios(user);
                const response = await signupRefreshApi.post(
                    '/api/users',
                    profileData
                );
                return response.data;
            },

            fetchProfile: async () => {
                const response = await api.get('/api/users/me');
                return response.data;
            },

            updateProfile: async (profileData) => {
                const response = await api.put('/api/users/me', profileData);
                return response.data;
            },

            updateEmailVerification: async (emailVerified) => {
                const response = await api.put('/api/users/me/verifyEmail', {
                    emailVerified,
                });
                return response.data;
            },

            // upload profile picture for the authenticated user
            uploadProfilePicture: async (formData) => {
                try {
                    const response = await api.post(
                        '/api/users/me/profile-picture',
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                            // Track upload progress
                            onUploadProgress: (progressEvent) => {
                                const percentCompleted = Math.round(
                                    (progressEvent.loaded * 100) /
                                        progressEvent.total
                                );
                                // eslint-disable-next-line no-console
                                console.log(
                                    `Upload progress: ${percentCompleted}%`
                                );
                            },
                        }
                    );

                    return response.data;
                } catch (error) {
                    console.error('Error uploading profile picture:', error);
                    throw error;
                }
            },

            // delete profile picture for the authenticated user
            deleteProfilePicture: async () => {
                try {
                    const response = await api.delete(
                        '/api/users/me/profile-picture'
                    );
                    return response.data;
                } catch (error) {
                    console.error('Error deleting profile picture:', error);
                    throw error;
                }
            },

            // fetch organizations the user is a member of
            fetchMyOrganizations: async () => {
                const response = await api.get('/api/users/me/organizations');
                return response.data;
            },

            // fetch ownership claims submitted by the user
            fetchMyClaims: async () => {
                const response = await api.get('/api/users/me/claims');
                return response.data;
            },

            // =========================================
            // AFFILIATION METHODS
            // =========================================

            // fetch authenticated user's affiliations
            fetchMyAffiliations: async () => {
                const response = await api.get('/api/users/me/affiliations');
                return response.data;
            },

            // create a new affiliation
            createAffiliation: async (affiliationData) => {
                const response = await api.post(
                    '/api/users/me/affiliations',
                    affiliationData
                );
                return response.data;
            },

            // update an existing affiliation
            updateAffiliation: async (orgId, affiliationData) => {
                const response = await api.put(
                    `/api/users/me/affiliations/${orgId}`,
                    affiliationData
                );
                return response.data;
            },

            // delete an affiliation
            deleteAffiliation: async (orgId) => {
                const response = await api.delete(
                    `/api/users/me/affiliations/${orgId}`
                );
                return response.data;
            },

            // fetch public affiliations for another user
            fetchUserAffiliations: async (userId) => {
                const response = await api.get(
                    `/api/users/${userId}/affiliations`
                );
                return response.data;
            },

            // =========================================
            // MENTOR PROFILE METHODS
            // =========================================

            updateMentorProfile: async (mentorData) => {
                const response = await api.put(
                    '/api/users/me/mentor-profile',
                    mentorData
                );
                return response.data;
            },

            fetchMentors: async (params) => {
                const response = await api.get('/api/users/mentors', {
                    params,
                });
                return response.data;
            },

            searchMentors: async (query) => {
                const response = await api.get('/api/users/mentors/search', {
                    params: { q: query },
                });
                return response.data;
            },

            fetchMentorCount: async () => {
                const response = await api.get('/api/users/mentors/count');
                return response.data;
            },

            fetchMemberCount: async () => {
                const response = await api.get('/api/users/count');
                return response.data;
            },

            // =========================================
            // ONBOARDING TOUR
            // =========================================

            markTourComplete: async () => {
                const response = await api.put('/api/users/me/tour-status', {
                    complete: true,
                });
                return response.data;
            },

            saveTourStep: async (tourStep) => {
                const response = await api.put('/api/users/me/tour-status', {
                    tourStep,
                });
                return response.data;
            },

            resetTour: async () => {
                const response = await api.put('/api/users/me/tour-status', {
                    reset: true,
                });
                return response.data;
            },
        }),
        [api, user]
    );
};

export default useUserApi;
